/* eslint-disable react/no-unescaped-entities */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';

// Material imports
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import MuiDialogTitle from '@mui/material/DialogTitle';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
// Components
import { MDialog } from 'common';

// Utils
import { pluralize } from 'feature/loading-management/utils/format';
import { ReconsignmentMessage, RestrictedLocationsWarning } from '../../../utils';

// Styles
import {
  dialogCloseButtonStyles,
  dialogHeaderStyles,
  dialogStyles,
  dividerLocationsStyles,
  expandButtonPanelStyles,
  hyperlinkStyles,
  listItemStyles,
  verticallyAlignedIconStyles,
} from '../Fulfillment.styles';

const ToggleSection = ({ setValue, reconsignment }) => {
  const [showDialog, toggleDialog] = useState(false);
  const [allowReconsignment, setAllowReconsignment] = useState(reconsignment);
  const theme = useTheme();

  const Dialog = () => {
    const DialogHeader = () => {
      return (
        <MuiDialogTitle sx={{ ...dialogHeaderStyles(theme) }}>
          <Typography variant="h6">Reconsignment control</Typography>
          <IconButton
            aria-label="close"
            onClick={() => toggleDialog(false)}
            size="large"
            sx={dialogCloseButtonStyles}
          >
            <CloseRounded />
          </IconButton>
        </MuiDialogTitle>
      );
    };
    return (
      <MDialog
        contentText={ReconsignmentMessage}
        header={<DialogHeader />}
        onClose={() => toggleDialog(false)}
        open={showDialog}
        sx={{ ...dialogStyles(theme) }}
      />
    );
  };

  const handleSetReconsignment = () => {
    setAllowReconsignment(!allowReconsignment);
    setValue('setReconsignmentControls', !allowReconsignment);
    setValue('convertBarrels', !allowReconsignment);
  };

  return (
    <Box display="flex" flexDirection="column">
      <Typography variant="h7">Do you want to restrict reconsignment?</Typography>
      <Box alignItems="center" display="flex" flexDirection="row" mt={2}>
        <Typography>No</Typography>
        <Switch checked={allowReconsignment} color="secondary" onChange={handleSetReconsignment} />
        <Typography>Yes</Typography>
        <Typography sx={{ ...hyperlinkStyles(theme) }} variant="caption">
          <Link color="secondary" onClick={() => toggleDialog(true)} sx={{ fontWeight: 400 }}>
            What's this?
          </Link>
          {showDialog && <Dialog />}
        </Typography>
      </Box>
    </Box>
  );
};

ToggleSection.propTypes = {
  reconsignment: bool.isRequired,
  setValue: func.isRequired,
};

const LocationMessage = ({ locations = [] }) => {
  const theme = useTheme();

  return locations.length ? (
    locations.map(({ nameShort }, i, arr) => `${nameShort}${i < arr.length - 1 ? ', ' : ''}`)
  ) : (
    <>
      <ErrorRoundedIcon
        sx={{
          color: 'error.main',
          fontSize: 32,
          ...verticallyAlignedIconStyles(theme),
        }}
      />
      {RestrictedLocationsWarning}
    </>
  );
};

LocationMessage.propTypes = {
  locations: arrayOf(
    shape({
      id: number,
      nameLong: string,
      nameShort: string,
    })
  ).isRequired,
};

const LocationListSection = ({ restrictedLocations }) => {
  const theme = useTheme();
  const { palette } = theme;

  const numberOfLocations = restrictedLocations.reduce((acc, origin) => {
    // eslint-disable-next-line no-param-reassign
    acc += origin.locations.length;
    return acc;
  }, 0);

  const someOriginsHaveNoLocations = restrictedLocations.some(({ locations }) => !locations.length);

  // if no locations  - automatically expand the dropdown, show the warning message and disable continue button
  const [showLocationList, setShowLocationList] = useState(someOriginsHaveNoLocations);

  const LocationList = () => {
    // const theme = useTheme();

    return (
      <List component="div">
        {restrictedLocations.map(({ nameShort, locations, id }) => (
          <ListItem key={id} sx={{ gutters: listItemStyles }}>
            <ListItemText
              primary={
                <Typography sx={{ color: 'text.disabled' }} variant="caption">
                  {nameShort}
                </Typography>
              }
              secondary={
                <Typography variant="body2">
                  <LocationMessage locations={locations} />
                </Typography>
              }
            />
          </ListItem>
        ))}
        <Divider sx={{ ...dividerLocationsStyles(theme) }} />
      </List>
    );
  };

  return (
    <>
      <Box
        bgcolor={showLocationList ? palette.surface.light : palette.surface.white}
        border="none"
        component="button"
        display="flex"
        justifyContent="space-between"
        onClick={() => setShowLocationList(!showLocationList)}
        sx={{ ...expandButtonPanelStyles(theme) }}
        width="100%"
      >
        <Box alignItems="start" display="flex" flexDirection="column">
          <Typography sx={{ color: palette.text.primary }} variant="body2">
            Restricted Locations List
          </Typography>
          <Typography sx={{ fontWeight: 400, mt: '2px' }} variant="caption">
            {`${pluralize(restrictedLocations.length, 'Origin')}, ${pluralize(
              numberOfLocations,
              'Location'
            )}`}
          </Typography>
        </Box>
        {showLocationList ? (
          <ExpandLessRoundedIcon color="secondary" data-testid="dropdown-button" />
        ) : (
          <ExpandMoreRoundedIcon color="secondary" data-testid="dropdown-button" />
        )}
      </Box>
      <Collapse in={showLocationList} unmountOnExit>
        <LocationList />
      </Collapse>
    </>
  );
};

LocationListSection.propTypes = {
  restrictedLocations: arrayOf(
    shape({
      id: number,
      locations: arrayOf(shape({ id: number, nameLong: string, nameShort: string })),
      nameLong: string,
      nameShort: string,
    })
  ).isRequired,
};

export { ToggleSection, LocationListSection };
